import React, { JSX, useCallback, useState } from "react";

import { ButtonStyle } from "components/buttons/button/button";
import { CollectionUtils } from "utilities/collection-utils";
import { EncodingType } from "models/enumerations/encoding/encoding-type";
import {
    GroupUsersService,
    GetCSVPreviewPathParams,
} from "utilities/services/groups/group-users-service";
import { FileRecord } from "models/view-models/files/file-record";
import { FileUtils } from "utilities/files/file-utils";
import { Modal, ModalAction } from "components/modal/modal";
import { ResultRecord } from "andculturecode-javascript-core";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization/t";
import { CreateBulkGroupUsersDtoRecord } from "../../../../../../models/view-models/groups/create-bulk-group-users-params-record";
import { BulkGroupUsersSummaryRecord } from "../../../../../../models/view-models/groups/bulk-group-users-summary-record";
import { AddBulkGroupUsersModalStage } from "../../../../../../models/enumerations/groups/add-bulk-group-users-modal-stage";
import { AddBulkGroupUsersForm } from "./add-bulk-group-users-form/add-bulk-group-users-form";
import { AddBulkGroupUsersPreview } from "./add-bulk-group-users-preview/add-bulk-group-users-preview";
import { AddBulkGroupUsersStatusForm } from "./add-bulk-group-users-status-form/add-bulk-group-users-status-form";
import { GroupUserUploadResultRecord } from "../../../../../../models/view-models/groups/group-user-upload-result-record";
import "./add-bulk-group-users-modal.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AddBulkGroupUsersModalProps {
    createBulkGroupUsersDto?: CreateBulkGroupUsersDtoRecord;
    handleSaveBulkGroupUsers: (
        createBulkGroupUsersDto: CreateBulkGroupUsersDtoRecord
    ) => Promise<ResultRecord<BulkGroupUsersSummaryRecord>> | any;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    groupId: number;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constant
// ------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "add-bulk-group-users-modal";

// #endregion Constants
// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AddBulkGroupUsersModal: React.FC<AddBulkGroupUsersModalProps> = (
    props: AddBulkGroupUsersModalProps
): JSX.Element => {
    let initialGroupUsers =
        props.createBulkGroupUsersDto ??
        new CreateBulkGroupUsersDtoRecord({ groupId: props.groupId });
    const encoding: EncodingType = FileUtils.getEncodingType();
    const [createBulkGroupUsersDto, setCreateBulkGroupUsersDto] = useState(initialGroupUsers);
    const [fileId, setFileId] = useState<number>(0);
    const [validGroupUsers, setValidGroupUsers] = useState<GroupUserUploadResultRecord[]>([]);
    const [invalidGroupUsers, setInvalidGroupUsers] = useState<GroupUserUploadResultRecord[]>([]);

    const [dirty, setDirty] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [bulkGroupUsersResult, setBulkGroupUsersResult] = useState<
        ResultRecord<BulkGroupUsersSummaryRecord>
    >(new ResultRecord<BulkGroupUsersSummaryRecord>());
    const [currentStage, setCurrentStage] = useState(AddBulkGroupUsersModalStage.Upload);

    const { list: csvPreviewApi } = GroupUsersService.useGetCSVPreview();

    const afterUploadStageModalActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalClose(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("done"),
            disabled: currentStage === AddBulkGroupUsersModalStage.Processing,
            onClick: () => {
                props.setOpen(false);
                setTimeout(() => {
                    setCurrentStage(AddBulkGroupUsersModalStage.Upload);
                }, 500);
            },
            style: ButtonStyle.Primary,
        },
    ];

    const confirmationActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => setShowConfirmationModal(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("confirm"),
            onClick: () => {
                setShowConfirmationModal(false);
                props.setOpen(false);
                resetForm();
                setTimeout(() => {
                    setCurrentStage(AddBulkGroupUsersModalStage.Upload);
                }, 500);
            },
            style: ButtonStyle.Destructive,
        },
    ];

    const previewStageModalActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalClose(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("saveValidUsers"),
            disabled: !validGroupUsers.length,
            onClick: () => onGroupUsersUpload(),
            style: ButtonStyle.Primary,
        },
    ];

    const uploadStageModalActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalClose(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("upload"),
            onClick: () => {
                getCSVPreview();
                setCurrentStage(AddBulkGroupUsersModalStage.Preview);
            },
            disabled: createBulkGroupUsersDto == null || fileId == null || fileId === 0,
            style: ButtonStyle.Primary,
        },
    ];

    const resetForm = (): void => {
        setDirty(false);
        setCreateBulkGroupUsersDto(initialGroupUsers);
        setInvalidGroupUsers([]);
        setValidGroupUsers([]);
        setFileId(0);
    };

    const onModalClose = (): void => {
        if (dirty) {
            setShowConfirmationModal(true);
        } else {
            props.setOpen(false);
        }
    };

    const onGroupUsersUpload = async (): Promise<void> => {
        setCurrentStage(AddBulkGroupUsersModalStage.Processing);

        var result = await props.handleSaveBulkGroupUsers(createBulkGroupUsersDto);

        resetForm();

        const succeeded = result?.resultObject != null && !result.hasErrors();
        const nextStage = succeeded
            ? AddBulkGroupUsersModalStage.Complete
            : AddBulkGroupUsersModalStage.Errored;
        setCurrentStage(nextStage);
        setBulkGroupUsersResult(result);
    };

    const onBulkGroupChanged = (createBulkGroupUsersDto: CreateBulkGroupUsersDtoRecord): void => {
        setDirty(true);
        setCreateBulkGroupUsersDto(createBulkGroupUsersDto);
    };

    const handleSetFileId = (file: FileRecord) => {
        if (file.id === undefined) {
            return;
        }
        setFileId(file.id);
    };

    const validateCsvFile = (file: File): boolean => {
        const fileExtensionValid: boolean = file.name.endsWith(".csv");

        return fileExtensionValid;
    };

    const handleInvalidFile = (): void => {
        ToastManager.error(t("youMustUploadACSVFile"));
    };

    const getCSVPreview = useCallback(async (): Promise<void> => {
        try {
            const csvPreviewPathParams: GetCSVPreviewPathParams = {
                fileId: fileId,
                groupId: props.groupId,
                encodingType: encoding,
            };
            const groupUsersResult = await csvPreviewApi(csvPreviewPathParams);
            if (groupUsersResult.result?.hasErrors() || groupUsersResult.resultObjects == null) {
                return;
            }

            const parsedGroupUsers = groupUsersResult.resultObjects;

            const parsedValidGroupUsers = parsedGroupUsers.filter(
                (gu) => !CollectionUtils.hasValues(gu.errors)
            );

            const parsedInvalidGroupUsers = parsedGroupUsers.filter((gu) =>
                CollectionUtils.hasValues(gu.errors)
            );

            setValidGroupUsers(parsedValidGroupUsers);
            setInvalidGroupUsers(parsedInvalidGroupUsers);

            onBulkGroupChanged(
                createBulkGroupUsersDto.with({
                    groupId: props.groupId,
                    groupUsers: parsedGroupUsers,
                })
            );

            setCurrentStage(AddBulkGroupUsersModalStage.Preview);
        } catch {
            ToastManager.error(t("errorParsingCSV"));
        }
    }, [createBulkGroupUsersDto, csvPreviewApi, encoding, fileId, props.groupId]);

    const modalActions = () => {
        switch (currentStage) {
            case AddBulkGroupUsersModalStage.Upload:
                return uploadStageModalActions;
            case AddBulkGroupUsersModalStage.Preview:
                return previewStageModalActions;
            case AddBulkGroupUsersModalStage.Processing:
                return afterUploadStageModalActions;
            default:
                return afterUploadStageModalActions;
        }
    };

    const modalBody = () => {
        switch (currentStage) {
            case AddBulkGroupUsersModalStage.Upload:
                return (
                    <AddBulkGroupUsersForm
                        fileId={fileId}
                        handleInvalidFile={handleInvalidFile}
                        onFileIdChange={handleSetFileId}
                        validateCsvFile={validateCsvFile}
                    />
                );
            case AddBulkGroupUsersModalStage.Preview:
                return (
                    <AddBulkGroupUsersPreview
                        validGroupUsers={validGroupUsers}
                        invalidGroupUsers={invalidGroupUsers}
                    />
                );
            default:
                return (
                    <AddBulkGroupUsersStatusForm
                        bulkGroupUsersResult={bulkGroupUsersResult!}
                        stage={currentStage}
                    />
                );
        }
    };

    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            isOpen={props.open}
            onModalClose={onModalClose}
            title={t("importGroupUsersCSV")}
            actions={modalActions()}
            modalStyle={""}>
            {modalBody()}
            <Modal
                isOpen={showConfirmationModal}
                onModalClose={() => {}}
                actions={confirmationActions}
                modalStyle={"-inverted"}>
                {t("youHaveUnsavedChanges")}
                <br></br> {t("areYouSureYouWantToExit")}
            </Modal>
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AddBulkGroupUsersModal };

// #endregion Exports
